'use client';
import React, { useMemo } from 'react'; // Import useMemo

import clsx from 'clsx';
import productDetail from '../../../styles/modules/product_detail.module.scss';

import * as C from "@components";
import SnapSlider from "@/components/SnapSlider/SnapSlider";
import VadImage from "vadimages-nextjs-image-optimizer";

export type HorizontalCardsItem = {
    title?: React.ReactNode,
    description: string,
    imageSrc: string,
    imageMobileSrc?: string,
    imageWidth: number,
    imageMobileWidth?: number,
    imageHeight: number,
    imageMobileHeight?: number,
    mobileImageSlideNode?: React.ReactNode,
    alt: string,
    video?: string;
    mobileVideo?: string;
}

export type HorizontalCardsProps = {
    items: HorizontalCardsItem[]
    className?: string;
}

// Memoize the component
const ProductHorizontalCards = React.memo(function ProductHorizontalCards(props: HorizontalCardsProps) {
    const data = useMemo(() => props.items, [props.items]); // Memoize items
    const dataLength = data.length;


    const classes = clsx(
        productDetail.horcard__list,
        props.className
    );

    // Was a Swiper carousel (slidesPerView:'auto', Navigation, Scrollbar). Converted to a native
    // CSS scroll-snap slider (SnapSlider) so Swiper leaves the bundle, with the live controls
    // rebuilt: draggable scrollbar + prev/next arrows (styled/positioned by the existing
    // product_detail.module.scss swiper rules). slidesPerView:'auto' -> natural slide width.
    return (<div className={classes}>
        <SnapSlider
            scrollbar
            navigation
            snap="mandatory"
            wrapItems={false}
            trackClassName="gap-[14px] md:gap-6"
        >
            {data.map((card: HorizontalCardsItem, index) => ( // Use memoized data
                <React.Fragment key={`card-${index}`}>
                    <div key={`first-${index}`} className="swiper-slide shrink-0 snap-start">
                        <div className={productDetail.horcard__list__item}>
                            <div className={productDetail.horcard__list__item__text}>
                                <div
                                    className={`counter ${productDetail.horcard__list__item_counter}`}>{index + 1} of {dataLength}</div>
                                {card.title}
                                <div className={productDetail.horcard__list__item_rte}
                                     dangerouslySetInnerHTML={{__html: card.description}}></div>
                            </div>
                        </div>
                    </div>
                    <div key={`second-${index}`} className="swiper-slide shrink-0 snap-start">
                        {!!card.mobileImageSlideNode ? card.mobileImageSlideNode : (
                                <>
                                    {card.video && 
                                        <C.InplaceVideo className={productDetail.video_wrapper} muted loop autoPlay={true}
                                                        imagePlaceholder={card.imageSrc}
                                                        imagePlaceholderAlt={card.alt}
                                                        imagePlaceholderWidth={card.imageWidth}
                                                        imagePlaceholderHeight={card.imageHeight}
                                        >
                                            <source src={card.video} type={"video/mp4"} />
                                        </C.InplaceVideo>
                                    }
                                    {card.video == null && card.mobileVideo && 
                                        <C.InplaceVideo className={productDetail.video_wrapper} muted loop autoPlay={true}
                                                        imagePlaceholder={card.imageMobileSrc}
                                                        imagePlaceholderAlt={card.alt}
                                                        imagePlaceholderWidth={card.imageMobileWidth}
                                                        imagePlaceholderHeight={card.imageMobileHeight}
                                        >
                                            <source src={card.mobileVideo} type={"video/mp4"} />
                                        </C.InplaceVideo>
                                    }
                                    {card.video == null && card.mobileVideo == null &&
                                        <div className={productDetail.horcard__list__item}>
                                            <figure className={productDetail.horcard__list__item__image}>
                                            <VadImage
                                                src={card.imageSrc}
                                                mobileSrc={card.imageMobileSrc}
                                                width={card.imageWidth}
                                                mobileWidth={card.imageMobileWidth}
                                                height={card.imageHeight}
                                                mobileHeight={card.imageMobileHeight}
                                                alt={card.alt}
                                                />
                                            </figure>
                                        </div>
                                    }
                                </>
                            )
                        }
                    </div>
                </React.Fragment>
            ))}
        </SnapSlider>
    </div>)
});

export default ProductHorizontalCards; // Export the memoized component
